/**
 * Pub-sub state management system for the chart.
 * Provides imperative API for real-time updates without React re-renders.
 */

import { createCompressedTimeScale } from "./compressedTimeScale";
import {
  alignSeriesToPrimaryRange,
  applyAutoDownsampling,
  detectGaps,
  fillGaps,
  getPointValue,
  getTimeDomain,
  getVolumeDomain,
  mergePoints,
  normalizeSeries,
  normalizeToPercentageChange,
  resampleToCommonGrid,
  trimToMaxPoints,
} from "./dataUtils";
import { getChartLogger } from "./debug";
import { getVisibleData } from "./hitTesting";
import { generateTimeSeparators, intervalToMs } from "./intervalUtils";
import { calculateLayout } from "./layout";
import { createLinearScale, createTimeScale, domainWithBuffer } from "./scales";
import {
  calculateAutoGrowViewport,
  calculateAutoScrollViewport,
  createViewport,
  isAtRightEdge,
  panByPixels,
  snapViewportToData,
  zoomByFactor,
  type ViewportConstraints,
} from "./viewport";
import type {
  ChartMode,
  ChartStore,
  ChartStoreConfig,
  ChartStoreEvent,
  ChartStoreListener,
  ChartStoreState,
  ComparisonConfig,
  DataConfig,
  DataGap,
  OhlcvPoint,
  PricePoint,
  ProcessedPricePoint,
  Scale,
  TickerSeries,
  TimeSeparator,
  Viewport,
} from "~types/types";

function getPointExtent(point: PricePoint | OhlcvPoint): {
  lo: number;
  hi: number;
} {
  if ("high" in point && "low" in point) {
    return { lo: point.low, hi: point.high };
  }

  return { lo: point.value, hi: point.value };
}

function computeYDomain(
  data: (PricePoint | OhlcvPoint)[],
  bufferRatio = 0.15,
  previousClose?: number
): [number, number] {
  if (data.length === 0) return [0, 100];

  let min = Infinity;
  let max = -Infinity;

  for (const point of data) {
    const { lo, hi } = getPointExtent(point);

    if (lo < min) min = lo;
    if (hi > max) max = hi;
  }

  // Include previousClose in the domain if provided
  if (previousClose !== undefined) {
    if (previousClose < min) min = previousClose;
    if (previousClose > max) max = previousClose;
  }

  if (min === Infinity || max === -Infinity) {
    return [0, 100];
  }

  return domainWithBuffer([min, max], bufferRatio);
}

function computeVolumeDomain(
  data: (PricePoint | OhlcvPoint)[]
): [number, number] {
  const volumeDomain = getVolumeDomain(data);

  return volumeDomain ?? [0, 100];
}

/**
 * Compute the base value for direction-based color decisions.
 * Uses previousClose when usePreviousCloseAsBaseline is set,
 * otherwise falls back to the first visible data point's value.
 */
function computeBaseValue(
  visibleData: (PricePoint | OhlcvPoint)[],
  previousClose: number | undefined,
  usePreviousCloseAsBaseline: boolean
): number {
  if (usePreviousCloseAsBaseline && previousClose !== undefined) {
    return previousClose;
  }

  if (visibleData.length >= 1) {
    const firstPoint = visibleData[0];

    if (firstPoint) {
      return getPointValue(firstPoint);
    }
  }

  return 0;
}

/**
 * Get normalized data for a series based on comparison config.
 * Returns normalized PricePoint[] for comparison mode, or original data for single mode.
 */
function getNormalizedSeriesData(
  series: TickerSeries,
  config: ComparisonConfig | undefined,
  mode: ChartMode,
  visibleRange?: { start: number; end: number }
): PricePoint[] {
  // Only normalize in comparison mode with a value mode specified
  if (mode !== "comparison" || !config?.valueMode) {
    // Runtime invariant: series data is homogeneous (all PricePoint or all OhlcvPoint)
    return series.priceMovements as PricePoint[];
  }

  const valueMode = config.valueMode;

  if (valueMode === "absolute") {
    // No normalization needed for absolute mode
    // Runtime invariant: series data is homogeneous (all PricePoint or all OhlcvPoint)
    return series.priceMovements as PricePoint[];
  }

  if (valueMode === "percentage") {
    return normalizeToPercentageChange(series, config, visibleRange);
  }

  if (valueMode === "normalized") {
    return normalizeSeries(series, config, visibleRange);
  }

  // Runtime invariant: series data is homogeneous (all PricePoint or all OhlcvPoint)
  return series.priceMovements as PricePoint[];
}

/**
 * Process raw series data according to dataConfig.
 * Applies normalization, gap detection, gap filling, and downsampling.
 */
function processSeriesData(
  series: TickerSeries,
  dataConfig: DataConfig | undefined,
  comparisonConfig: ComparisonConfig | undefined,
  mode: ChartMode,
  chartWidth: number
): {
  processedData: ProcessedPricePoint[];
  gaps: DataGap[];
} {
  // Runtime invariant: series data is homogeneous (all PricePoint or all OhlcvPoint)
  let data = series.priceMovements as PricePoint[];

  // Step 1: Apply normalization based on dataConfig or comparisonConfig
  if (dataConfig?.normalization === "percentage") {
    data = normalizeToPercentageChange(series, comparisonConfig ?? {});
  } else if (dataConfig?.normalization === "indexed") {
    data = normalizeSeries(series, comparisonConfig ?? {});
  } else if (mode === "comparison" && comparisonConfig?.valueMode) {
    // Fall back to comparison config normalization
    data = getNormalizedSeriesData(series, comparisonConfig, mode);
  }

  // Step 2: Detect gaps if interval is specified
  let gaps: DataGap[] = [];

  if (dataConfig?.interval) {
    const intervalMs = intervalToMs(dataConfig.interval);

    gaps = detectGaps(data, intervalMs);
  }

  // Step 3: Fill gaps if enabled (default: true)
  let processed: ProcessedPricePoint[] = data.map((p) => ({ ...p }));

  if (
    dataConfig?.interval &&
    dataConfig.fillGaps !== false &&
    gaps.length > 0
  ) {
    const intervalMs = intervalToMs(dataConfig.interval);

    processed = fillGaps(data, gaps, intervalMs);
  }

  // Step 4: Downsample if enabled (default: true)
  if (dataConfig?.autoDownsample !== false && chartWidth > 0) {
    processed = applyAutoDownsampling(processed, chartWidth);
  }

  return { processedData: processed, gaps };
}

/**
 * Align comparison series to the primary ticker's time range.
 * Updates both seriesMeta and seriesData maps so that domain computation
 * and data processing use consistent time boundaries.
 */
function alignComparisonSeries(
  seriesMeta: Map<string, TickerSeries>,
  seriesData: Map<string, (PricePoint | OhlcvPoint)[]>
): void {
  if (seriesMeta.size <= 1) return;

  const seriesEntries = Array.from(seriesMeta.entries());
  const primaryData = seriesEntries[0]?.[1]?.priceMovements as PricePoint[];

  if (!primaryData?.length) return;

  const primaryDomain = getTimeDomain(primaryData);

  if (!primaryDomain) return;

  const [primaryStart, primaryEnd] = primaryDomain;

  for (let i = 1; i < seriesEntries.length; i++) {
    const entry = seriesEntries[i];

    if (!entry) continue;

    const [seriesId, series] = entry;
    const data = series.priceMovements as PricePoint[];
    const aligned = alignSeriesToPrimaryRange(data, primaryStart, primaryEnd);

    seriesMeta.set(seriesId, { ...series, priceMovements: aligned });
    seriesData.set(seriesId, aligned);
  }
}

function processAllSeriesData(
  seriesMeta: Map<string, TickerSeries>,
  seriesData: Map<string, (PricePoint | OhlcvPoint)[]>,
  dataConfig: DataConfig | undefined,
  comparisonConfig: ComparisonConfig | undefined,
  mode: ChartMode,
  chartWidth: number
): {
  processedData: Map<string, ProcessedPricePoint[]>;
  dataGaps: Map<string, DataGap[]>;
} {
  const processedData = new Map<string, ProcessedPricePoint[]>();
  const dataGaps = new Map<string, DataGap[]>();

  // Align comparison series to primary ticker's time range
  if (mode === "comparison") {
    alignComparisonSeries(seriesMeta, seriesData);
  }

  for (const [seriesId, series] of seriesMeta) {
    const result = processSeriesData(
      series,
      dataConfig,
      comparisonConfig,
      mode,
      chartWidth
    );

    processedData.set(seriesId, result.processedData);
    dataGaps.set(seriesId, result.gaps);
  }

  // For comparison mode, resample all series to common time grid
  if (mode === "comparison" && processedData.size > 1 && dataConfig?.interval) {
    const resampled = resampleToCommonGrid(processedData);

    return { processedData: resampled, dataGaps };
  }

  return { processedData, dataGaps };
}

/**
 * Extract sorted timestamps from all series data.
 */
function getAllTimestamps(state: ChartStoreState): number[] {
  const allData = Array.from(state.seriesData.values()).flat();
  const timestamps = allData.map((p) => p.timestamp);

  // Remove duplicates and sort
  return [...new Set(timestamps)].sort((a, b) => a - b);
}

function computeDataEndWithBuffer(
  state: ChartStoreState,
  dataEnd: number,
  dataRange: number,
  bufferRatio: number
): number {
  if (!state.dataConfig?.interval) {
    return dataEnd + dataRange * bufferRatio;
  }

  const intervalMs = intervalToMs(state.dataConfig.interval);

  if (intervalMs > 30 * 60 * 1000) {
    return dataEnd + dataRange * bufferRatio;
  }

  // Intraday: align to next interval boundary
  const baseBuffer = dataRange * bufferRatio;
  const endWithBaseBuffer = dataEnd + baseBuffer;
  const remainder = endWithBaseBuffer % intervalMs;

  return remainder === 0
    ? endWithBaseBuffer
    : endWithBaseBuffer + (intervalMs - remainder);
}

function computeMinRange(state: ChartStoreState, dataRange: number): number {
  if (!state.dataConfig?.interval) {
    return dataRange * 0.005;
  }

  return intervalToMs(state.dataConfig.interval) * 5;
}

function getConstraints(state: ChartStoreState): ViewportConstraints {
  const allData = Array.from(state.seriesData.values()).flat();
  const timeDomain = getTimeDomain(allData);

  if (!timeDomain) {
    return {
      minRange: 60000,
      maxRange: 86400000,
      dataStart: 0,
      dataEnd: 1000,
    };
  }

  const [dataStart, dataEnd] = timeDomain;
  const dataRange = dataEnd - dataStart;
  const bufferRatio = state.viewportEndBuffer;

  return {
    minRange: computeMinRange(state, dataRange),
    maxRange: dataRange * (1 + bufferRatio),
    dataStart,
    dataEnd: computeDataEndWithBuffer(state, dataEnd, dataRange, bufferRatio),
    allowOverscroll: false,
    overscrollRatio: 0,
  };
}

function collectYDomainData(
  state: ChartStoreState,
  _config: ChartStoreConfig
): PricePoint[] {
  if (
    state.mode === "comparison" &&
    state.comparisonConfig?.valueMode &&
    state.comparisonConfig.valueMode !== "absolute"
  ) {
    const allNormalizedData: PricePoint[] = [];

    for (const [, series] of state.seriesMeta) {
      const normalizedSeriesData = getNormalizedSeriesData(
        series,
        state.comparisonConfig,
        state.mode,
        state.viewport
      );

      allNormalizedData.push(...normalizedSeriesData);
    }

    return allNormalizedData;
  }

  // Runtime invariant: series data is homogeneous (all PricePoint or all OhlcvPoint)
  return Array.from(state.seriesData.values()).flat() as PricePoint[];
}

function computeXScale(state: ChartStoreState, allGaps: DataGap[]): Scale {
  const hasLargeGaps = allGaps.some((g) => g.isLarge);
  const useCompressedScale =
    state.dataConfig?.fillGaps === false &&
    hasLargeGaps &&
    state.dataConfig?.interval;

  const range: [number, number] = [
    state.layout.priceArea.x,
    state.layout.priceArea.x + state.layout.priceArea.width,
  ];

  if (useCompressedScale) {
    return createCompressedTimeScale({
      domain: [state.viewport.start, state.viewport.end],
      range,
      gaps: allGaps,
    });
  }

  return createTimeScale({
    domain: [state.viewport.start, state.viewport.end],
    range,
  });
}

type VolumeScaleResult = {
  volumeDomain: [number, number];
  volumeScale: Scale;
};

function computeVolumeScaleIfEnabled(
  state: ChartStoreState,
  config: ChartStoreConfig
): VolumeScaleResult | null {
  if (!config.volumeEnabled) return null;

  const rawData = Array.from(state.seriesData.values()).flat();
  const rawVisibleData = rawData.filter(
    (p) =>
      p.timestamp >= state.viewport.start && p.timestamp <= state.viewport.end
  );

  const volumeDomain = computeVolumeDomain(
    rawVisibleData.length > 0 ? rawVisibleData : rawData
  );

  const volumeTop =
    state.layout.priceArea.y + state.layout.priceArea.height * 0.7;
  const volumeBottom = state.layout.priceArea.y + state.layout.priceArea.height;

  return {
    volumeDomain,
    volumeScale: createLinearScale({
      domain: volumeDomain,
      range: [volumeBottom, volumeTop],
    }),
  };
}

function computeAutoScrollViewport(
  merged: (PricePoint | OhlcvPoint)[],
  config: ChartStoreConfig,
  currentViewport: Viewport,
  constraints: ViewportConstraints
): Viewport | null {
  if (!config.realtime?.autoScroll || merged.length === 0) {
    return null;
  }

  const latestPoint = merged.at(-1);

  if (
    !latestPoint ||
    !isAtRightEdge(currentViewport, latestPoint.timestamp, 1000)
  ) {
    return null;
  }

  const behavior = config.realtime.autoScrollBehavior ?? "grow";
  const bufferRatio = config.realtime.bufferRatio ?? 0.1;

  return behavior === "grow"
    ? calculateAutoGrowViewport(
        currentViewport,
        latestPoint.timestamp,
        bufferRatio,
        constraints
      )
    : calculateAutoScrollViewport(
        currentViewport,
        latestPoint.timestamp,
        constraints
      );
}

/**
 * Create a new chart store instance.
 */
export function createChartStore(config: ChartStoreConfig): ChartStore {
  const listeners = new Map<ChartStoreEvent, Set<ChartStoreListener>>();

  const events: ChartStoreEvent[] = [
    "data",
    "viewport",
    "scales",
    "hover",
    "layout",
    "config",
  ];

  events.forEach((event) => listeners.set(event, new Set()));

  const seriesData = new Map<string, (PricePoint | OhlcvPoint)[]>();
  const seriesMeta = new Map<string, TickerSeries>();

  config.series.forEach((series) => {
    seriesData.set(series.id, [...series.priceMovements]);
    seriesMeta.set(series.id, series);
  });

  // Align comparison series before domain computation so viewport
  // is constrained to the primary ticker's time range
  if (config.mode === "comparison") {
    alignComparisonSeries(seriesMeta, seriesData);
  }

  const layout = calculateLayout({
    width: config.width,
    height: config.height,
    padding: config.padding,
    volume: config.volumeEnabled
      ? { enabled: true, heightRatio: config.volumeHeightRatio }
      : undefined,
    yAxisPosition: config.yAxisPosition,
  });

  const allData = Array.from(seriesData.values()).flat();
  const timeDomain = getTimeDomain(allData);
  const [dataStart, dataEnd] = timeDomain ?? [0, 1000];

  // Viewport end buffer: false = 0, undefined = 0.1 (default), number = custom ratio
  const viewportEndBuffer =
    config.viewportEndBuffer === false ? 0 : config.viewportEndBuffer ?? 0.1;

  const dataRange = dataEnd - dataStart;
  const initialEndWithBuffer = dataEnd + dataRange * viewportEndBuffer;

  const viewport = config.initialViewport
    ? createViewport(dataStart, dataEnd, config.initialViewport)
    : createViewport(dataStart, initialEndWithBuffer);

  const xScale = createTimeScale({
    domain: [viewport.start, viewport.end],
    range: [layout.priceArea.x, layout.priceArea.x + layout.priceArea.width],
  });

  // For comparison mode, use normalized data for Y-domain computation
  let dataForYDomain: PricePoint[];

  if (
    config.mode === "comparison" &&
    config.comparisonConfig?.valueMode &&
    config.comparisonConfig.valueMode !== "absolute"
  ) {
    // Collect normalized data from all series
    const allNormalizedData: PricePoint[] = [];

    for (const series of config.series) {
      const normalizedSeriesData = getNormalizedSeriesData(
        series,
        config.comparisonConfig,
        config.mode,
        viewport
      );

      allNormalizedData.push(...normalizedSeriesData);
    }
    dataForYDomain = allNormalizedData;
  } else {
    // Runtime invariant: series data is homogeneous (all PricePoint or all OhlcvPoint)
    dataForYDomain = allData as PricePoint[];
  }

  const visibleData = dataForYDomain.filter(
    (p) => p.timestamp >= viewport.start && p.timestamp <= viewport.end
  );

  // Include previousClose in y-domain only when explicitly using it as baseline
  const firstSeries = config.series[0];
  const previousClose =
    config.mode === "single" && config.usePreviousCloseAsBaseline
      ? firstSeries?.previousClose
      : undefined;

  const yDomain = computeYDomain(
    visibleData.length > 0 ? visibleData : dataForYDomain,
    0.15,
    previousClose
  );

  const baseValue = computeBaseValue(
    visibleData.length > 0 ? visibleData : dataForYDomain,
    previousClose,
    config.usePreviousCloseAsBaseline ?? false
  );

  const yScale = createLinearScale({
    domain: yDomain,
    range: [layout.priceArea.y + layout.priceArea.height, layout.priceArea.y],
  });

  let volumeScale: Scale | null = null;
  let volumeDomain: [number, number] | null = null;

  if (config.volumeEnabled) {
    // Volume always uses raw data
    const rawVisibleData = allData.filter(
      (p) => p.timestamp >= viewport.start && p.timestamp <= viewport.end
    );

    volumeDomain = computeVolumeDomain(
      rawVisibleData.length > 0 ? rawVisibleData : allData
    );

    // Volume overlay occupies bottom ~30% of price area
    const volumeTop = layout.priceArea.y + layout.priceArea.height * 0.7;
    const volumeBottom = layout.priceArea.y + layout.priceArea.height;

    volumeScale = createLinearScale({
      domain: volumeDomain,
      range: [volumeBottom, volumeTop],
    });
  }

  // Process series data according to dataConfig
  const { processedData, dataGaps } = processAllSeriesData(
    seriesMeta,
    seriesData,
    config.dataConfig,
    config.comparisonConfig,
    config.mode,
    layout.priceArea.width
  );

  // Generate time separators if enabled
  let timeSeparators: TimeSeparator[] = [];

  if (config.dataConfig?.showTimeSeparators !== false) {
    timeSeparators = generateTimeSeparators(viewport.start, viewport.end);
  }

  const state: ChartStoreState = {
    mode: config.mode,
    chartType: config.chartType,
    theme: config.theme ?? "light",
    comparisonConfig: config.comparisonConfig,
    dataConfig: config.dataConfig,
    // Store is always recreated when config changes (via useChartStore),
    // so initialization here covers all intraday updates.
    intraday: config.intraday ?? false,
    viewportEndBuffer,
    seriesData,
    seriesMeta,
    processedData,
    dataGaps,
    timeSeparators,
    layout,
    viewport,
    xScale,
    yScale,
    volumeScale,
    yDomain,
    volumeDomain,
    baseValue,
    hover: {
      visible: false,
      screenX: 0,
      screenY: 0,
      chartX: 0,
      chartY: 0,
      timestamp: null,
      nearestPoints: new Map(),
    },
    isDragging: false,
    isPinching: false,
  };

  const logger = getChartLogger();

  const dispatch = (event: ChartStoreEvent): void => {
    const listenerCount = listeners.get(event)?.size ?? 0;

    logger.debug(`dispatch('${event}'), listeners: ${listenerCount}`);
    listeners.get(event)?.forEach((listener) => listener());
  };

  // Event batching to prevent multiple re-renders from sequential dispatches
  const batchedEvents = new Set<ChartStoreEvent>();
  let batchFlushScheduled = false;

  const flushBatch = (): void => {
    batchFlushScheduled = false;
    const eventsToDispatch = [...batchedEvents];

    batchedEvents.clear();
    eventsToDispatch.forEach((event) => dispatch(event));
  };

  const batchDispatch = (event: ChartStoreEvent): void => {
    batchedEvents.add(event);
    if (!batchFlushScheduled) {
      batchFlushScheduled = true;
      queueMicrotask(flushBatch);
    }
  };

  const throttledDispatchers = new Map<
    ChartStoreEvent,
    { pending: boolean; frameId: number | null }
  >();

  const throttledDispatch = (event: ChartStoreEvent): void => {
    let dispatchState = throttledDispatchers.get(event);

    if (!dispatchState) {
      dispatchState = { pending: false, frameId: null };
      throttledDispatchers.set(event, dispatchState);
    }

    if (dispatchState.pending) return;

    dispatchState.pending = true;
    // Use requestAnimationFrame for optimal frame timing instead of setTimeout
    if (typeof requestAnimationFrame !== "undefined") {
      dispatchState.frameId = requestAnimationFrame(() => {
        dispatch(event);
        if (dispatchState) {
          dispatchState.pending = false;
        }
      });
    } else {
      // Fallback for non-browser environments (e.g., SSR, tests)
      dispatchState.frameId = globalThis.setTimeout(() => {
        dispatch(event);
        if (dispatchState) {
          dispatchState.pending = false;
        }
      }, 16) as unknown as number;
    }
  };

  const recomputeScales = (): void => {
    const yDomainData = collectYDomainData(state, config);

    const currentVisibleData = yDomainData.filter(
      (p) =>
        p.timestamp >= state.viewport.start && p.timestamp <= state.viewport.end
    );

    const allGaps = Array.from(state.dataGaps.values()).flat();

    state.xScale = computeXScale(state, allGaps);

    if (state.dataConfig?.showTimeSeparators !== false) {
      state.timeSeparators = generateTimeSeparators(
        state.viewport.start,
        state.viewport.end
      );
    }

    const firstSeriesMeta = state.seriesMeta.values().next().value;
    const prevClose =
      state.mode === "single" && config.usePreviousCloseAsBaseline
        ? firstSeriesMeta?.previousClose
        : undefined;

    const newYDomain = computeYDomain(
      currentVisibleData.length > 0 ? currentVisibleData : yDomainData,
      0.15,
      prevClose
    );

    state.yDomain = newYDomain;

    state.yScale = createLinearScale({
      domain: newYDomain,
      range: [
        state.layout.priceArea.y + state.layout.priceArea.height,
        state.layout.priceArea.y,
      ],
    });

    state.baseValue = computeBaseValue(
      currentVisibleData.length > 0 ? currentVisibleData : yDomainData,
      prevClose,
      config.usePreviousCloseAsBaseline ?? false
    );

    const volumeResult = computeVolumeScaleIfEnabled(state, config);

    if (volumeResult) {
      state.volumeDomain = volumeResult.volumeDomain;
      state.volumeScale = volumeResult.volumeScale;
    }
  };

  const reprocessData = (): void => {
    const { processedData: newProcessed, dataGaps: newGaps } =
      processAllSeriesData(
        state.seriesMeta,
        state.seriesData,
        state.dataConfig,
        state.comparisonConfig,
        state.mode,
        state.layout.priceArea.width
      );

    state.processedData = newProcessed;
    state.dataGaps = newGaps;
  };

  return {
    getState: () => state,

    getSeriesData: (seriesId) => state.seriesData.get(seriesId) ?? [],

    getVisibleData: (seriesId) => {
      // Returns only truly visible data (no padding) for calculations like color direction
      if (state.dataConfig) {
        const processed = state.processedData.get(seriesId);

        if (!processed) return [];

        return getVisibleData(processed, state.viewport, 0).data;
      }

      const series = state.seriesMeta.get(seriesId);

      if (!series) {
        return [];
      }

      const normalizedData = getNormalizedSeriesData(
        series,
        state.comparisonConfig,
        state.mode,
        state.viewport
      );

      return getVisibleData(normalizedData, state.viewport, 0).data;
    },

    getRenderableData: (seriesId) => {
      // Return all processed data — visual bounds are enforced by the SVG clipPath in PriceSeries
      if (state.dataConfig) {
        return state.processedData.get(seriesId) ?? [];
      }

      // Fallback: comparison normalization without dataConfig — viewport-filtered since no clipPath
      const series = state.seriesMeta.get(seriesId);

      if (!series) return [];

      const normalizedData = getNormalizedSeriesData(
        series,
        state.comparisonConfig,
        state.mode,
        state.viewport
      );

      return getVisibleData(normalizedData, state.viewport, 1).data;
    },

    getHoverState: () => state.hover,
    getViewport: () => state.viewport,
    getScales: () => ({
      x: state.xScale,
      y: state.yScale,
      volume: state.volumeScale,
    }),
    getLayout: () => state.layout,
    getTimeSeparators: () => state.timeSeparators,
    getDataGaps: (seriesId) => state.dataGaps.get(seriesId) ?? [],

    subscribe: (event, listener) => {
      listeners.get(event)?.add(listener);

      return () => listeners.get(event)?.delete(listener);
    },

    subscribeMany: (eventsList, listener) => {
      eventsList.forEach((event) => listeners.get(event)?.add(listener));

      return () =>
        eventsList.forEach((event) => listeners.get(event)?.delete(listener));
    },

    appendData: (seriesId, points) => {
      const existing = state.seriesData.get(seriesId) ?? [];
      let merged = mergePoints(existing, points);

      if (config.realtime?.maxPoints) {
        merged = trimToMaxPoints(merged, config.realtime.maxPoints);
      }

      state.seriesData.set(seriesId, merged);

      // Update seriesMeta with new data
      const series = state.seriesMeta.get(seriesId);

      if (series) {
        state.seriesMeta.set(seriesId, {
          ...series,
          // Type assertion: runtime data is homogeneous (all PricePoint or all OhlcvPoint)
          priceMovements: merged as PricePoint[] | OhlcvPoint[],
        });
      }

      // Reprocess data if dataConfig is present
      if (state.dataConfig) {
        reprocessData();
      }

      recomputeScales();

      if (config.realtime?.throttleMs) {
        throttledDispatch("data");
        throttledDispatch("scales");
      } else {
        batchDispatch("data");
        batchDispatch("scales");
      }

      const scrolledViewport = computeAutoScrollViewport(
        merged,
        config,
        state.viewport,
        getConstraints(state)
      );

      if (scrolledViewport) {
        state.viewport = scrolledViewport;
        recomputeScales();
        dispatch("viewport");
      }
    },

    setSeriesData: (seriesId, data) => {
      state.seriesData.set(seriesId, data);

      // Update seriesMeta with new data
      const series = state.seriesMeta.get(seriesId);

      if (series) {
        state.seriesMeta.set(seriesId, {
          ...series,
          // Type assertion: runtime data is homogeneous (all PricePoint or all OhlcvPoint)
          priceMovements: data as PricePoint[] | OhlcvPoint[],
        });
      }

      // Reprocess data if dataConfig is present
      if (state.dataConfig) {
        reprocessData();
      }

      recomputeScales();
      batchDispatch("data");
      batchDispatch("scales");
    },

    addSeries: (series) => {
      state.seriesData.set(series.id, [...series.priceMovements]);
      state.seriesMeta.set(series.id, series);

      // Reprocess data if dataConfig is present
      if (state.dataConfig) {
        reprocessData();
      }

      recomputeScales();
      batchDispatch("data");
      batchDispatch("scales");
    },

    removeSeries: (seriesId) => {
      state.seriesData.delete(seriesId);
      state.seriesMeta.delete(seriesId);
      state.processedData.delete(seriesId);
      state.dataGaps.delete(seriesId);

      // Reprocess remaining data if dataConfig is present (for comparison resampling)
      if (state.dataConfig && state.mode === "comparison") {
        reprocessData();
      }

      recomputeScales();
      batchDispatch("data");
      batchDispatch("scales");
    },

    setViewport: (newViewport) => {
      // Snap viewport boundaries to nearest data points
      const timestamps = getAllTimestamps(state);
      const constraints = getConstraints(state);

      state.viewport = snapViewportToData(newViewport, timestamps, constraints);
      recomputeScales();
      batchDispatch("viewport");
      batchDispatch("scales");
    },

    setViewportWithoutSnap: (newViewport) => {
      // Apply constraints but don't snap to data points
      const constraints = getConstraints(state);
      const range = newViewport.end - newViewport.start;

      // Clamp range to min/max
      const clampedRange = Math.max(
        constraints.minRange,
        Math.min(constraints.maxRange, range)
      );

      // Clamp position to data bounds
      let start = newViewport.start;
      let end = start + clampedRange;

      if (start < constraints.dataStart) {
        start = constraints.dataStart;
        end = start + clampedRange;
      }
      if (end > constraints.dataEnd) {
        end = constraints.dataEnd;
        start = end - clampedRange;
      }

      state.viewport = { start, end };
      recomputeScales();
      batchDispatch("viewport");
      batchDispatch("scales");
    },

    pan: (deltaX) => {
      const pixelsPerMs =
        state.layout.priceArea.width /
        (state.viewport.end - state.viewport.start);
      const constraints = getConstraints(state);
      const result = panByPixels(
        state.viewport,
        deltaX,
        pixelsPerMs,
        constraints
      );

      // Snap viewport boundaries to nearest data points
      const timestamps = getAllTimestamps(state);

      state.viewport = snapViewportToData(
        result.viewport,
        timestamps,
        constraints
      );
      recomputeScales();
      batchDispatch("viewport");
      batchDispatch("scales");
    },

    panWithoutSnap: (deltaX) => {
      const pixelsPerMs =
        state.layout.priceArea.width /
        (state.viewport.end - state.viewport.start);
      const constraints = getConstraints(state);
      const result = panByPixels(
        state.viewport,
        deltaX,
        pixelsPerMs,
        constraints
      );

      // No snapping during pan - allows smooth drag movement
      state.viewport = result.viewport;
      recomputeScales();
      batchDispatch("viewport");
      batchDispatch("scales");
    },

    snapViewport: () => {
      const timestamps = getAllTimestamps(state);
      const constraints = getConstraints(state);

      state.viewport = snapViewportToData(
        state.viewport,
        timestamps,
        constraints
      );
      recomputeScales();
      batchDispatch("viewport");
      batchDispatch("scales");
    },

    zoom: (factor, anchorX) => {
      const anchorTimestamp = state.xScale.invert(anchorX);
      const constraints = getConstraints(state);
      const result = zoomByFactor(
        state.viewport,
        factor,
        anchorTimestamp,
        constraints
      );

      // No snapping during zoom - allows smooth zooming
      state.viewport = result.viewport;

      recomputeScales();
      batchDispatch("viewport");
      batchDispatch("scales");
    },

    resetView: () => {
      const resetData = Array.from(state.seriesData.values()).flat();
      const resetTimeDomain = getTimeDomain(resetData);

      if (resetTimeDomain) {
        state.viewport = { start: resetTimeDomain[0], end: resetTimeDomain[1] };
        recomputeScales();
        dispatch("viewport");
        dispatch("scales");
      }
    },

    setHover: (partial) => {
      Object.assign(state.hover, partial);
      throttledDispatch("hover");
    },

    clearHover: () => {
      state.hover.visible = false;
      state.hover.nearestPoints.clear();
      dispatch("hover");
    },

    setDimensions: (width, height) => {
      const newLayout = calculateLayout({
        width,
        height,
        padding: config.padding,
        volume: config.volumeEnabled
          ? { enabled: true, heightRatio: config.volumeHeightRatio }
          : undefined,
        yAxisPosition: config.yAxisPosition,
      });

      state.layout = newLayout;
      recomputeScales();
      batchDispatch("layout");
      batchDispatch("scales");
    },

    setDragging: (isDragging) => {
      state.isDragging = isDragging;
    },

    setPinching: (isPinching) => {
      state.isPinching = isPinching;
    },

    destroy: () => {
      listeners.forEach((set) => set.clear());
      throttledDispatchers.forEach((dispatchState) => {
        if (dispatchState.frameId) {
          if (cancelAnimationFrame) {
            cancelAnimationFrame(dispatchState.frameId);
          } else {
            clearTimeout(dispatchState.frameId);
          }
        }
      });
    },
  };
}
